import cn from 'clsx';
import React, {
  ButtonHTMLAttributes,
  forwardRef,
  JSXElementConstructor,
  useMemo,
  useRef,
} from 'react';
import mergeRefs from 'react-merge-refs';

import s from '../../../styles/ui/Button/Button.module.css';
import { Loader } from '../index';

export type ButtonVariant = 'naked' | 'primary' | 'secondary' | 'ghost' | 'wizard' | 'dark';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  href?: string;
  className?: string;
  variant?: ButtonVariant;
  active?: boolean;
  inverted?: boolean;
  rounded?: boolean;
  size?: 'normal' | 'small' | 'large' | 'tiny';
  type?: 'submit' | 'reset' | 'button';
  Component?: string | JSXElementConstructor<any>;
  loading?: boolean;
  danger?: boolean;
  stretch?: boolean;
  preIcon?: any;
  postIcon?: any;
  disabled?: boolean;
}

const Button: React.FC<ButtonProps> = forwardRef((props, buttonRef) => {
  const {
    className,
    variant = 'primary',
    children,
    size = 'normal',
    rounded,
    inverted,
    stretch,
    active,
    preIcon,
    postIcon,
    loading = false,
    disabled = false,
    danger,
    Component = 'button',
    onClick,
    ...rest
  } = props;
  const ref = useRef<typeof Component>(null);
  const rootClassName = cn(
    s.root,
    {
      [s.primary]: variant === 'primary',
      [s.secondary]: variant === 'secondary',
      [s.naked]: variant === 'naked',
      [s.danger]: danger,
      [s.ghost]: variant === 'ghost',
      [s.dark]: variant === 'dark',
      [s.wizard]: variant === 'wizard',
      [s.small]: size === 'small',
      [s.tiny]: size === 'tiny',
      [s.large]: size === 'large',
      [s.stretch]: stretch,
      [s.secondaryActive]: variant === 'secondary' && active,
      [s.rounded]: rounded,
      [s.inverted]: inverted,
      [s.loading]: loading,
      [s.disabled]: disabled,
    },
    className,
  );
  const loaderColor = useMemo(() => {
    variant === 'secondary' ? '#1B2124' : '#fff';
    if (variant === 'secondary') {
      return '#fff';
    }
    if (variant === 'naked') {
      return 'rgb(81 68 196)';
    }
    return '#fff';
  }, [variant]);
  return (
    <Component
      aria-pressed={active}
      active={active ? 'true' : ''}
      data-variant={variant}
      ref={mergeRefs([ref, buttonRef])}
      className={rootClassName}
      disabled={disabled}
      onClick={onClick}
      {...rest}
    >
      {loading && <Loader color={loaderColor} />}
      {preIcon && <span className={'pr-1'}>{preIcon}</span>}
      {children}
      {postIcon && <span className={'pl-1'}>{postIcon}</span>}
    </Component>
  );
});

export default Button;
